/* eslint-disable */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { authApi } from "./api";

const AuthContext = createContext(null);

// The active-role scope this session is currently acting as. The backend
// returns it as `active_role` on every auth response and enforces it via the
// signed `act` JWT claim; this is just the client mirror. Falls back for
// legacy user blobs minted before the role switcher shipped.
function resolveActiveRole(user) {
  if (!user) return null;
  if (user.active_role) return user.active_role;
  return user.is_admin ? "ADMIN" : "RESIDENT";
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

// Decode the access_token's `imp` claim — if present, the current
// session is an IT_ADMIN impersonating someone. The banner + nav
// reads this. We don't verify the signature here (the backend does
// that on every request); this is purely a UX hint.
function decodeImpClaim() {
  try {
    const tok = localStorage.getItem("access_token");
    if (!tok) return null;
    const payload = tok.split(".")[1];
    if (!payload) return null;
    // base64url → base64
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64));
    return json.imp || null;
  } catch (_) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(readStoredUser);
  const [impersonatorId, setImpersonatorId] = useState(decodeImpClaim);

  const setUser = useCallback((next) => {
    setUserState(next);
    // Whenever the user changes, re-read the impersonation claim too —
    // it travels in the access token, not in the user payload.
    setImpersonatorId(decodeImpClaim());
    if (next) {
      try {
        localStorage.setItem("user", JSON.stringify(next));
      } catch (_) {}
      sessionStorage.removeItem("auth_error");
    } else {
      try {
        localStorage.removeItem("user");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
      } catch (_) {}
    }
  }, []);

  // Cross-tab sync. When the user logs out in another tab, mirror it
  // here. Same for logging in.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "user") {
        setUserState(readStoredUser());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Session expiry from the API client → drop user state.
  useEffect(() => {
    const onAuthErr = () => setUser(null);
    window.addEventListener("vs_auth_error", onAuthErr);
    return () => window.removeEventListener("vs_auth_error", onAuthErr);
  }, [setUser]);

  // Switch the active-role scope of the current session. Persists the
  // re-minted token pair + updated user blob, then updates state so the
  // whole tree re-renders against the new view.
  const switchRole = useCallback(
    async (role) => {
      const token = localStorage.getItem("access_token");
      const res = await authApi.switchRole(role, token);
      try {
        localStorage.setItem("access_token", res.access_token);
        localStorage.setItem("refresh_token", res.refresh_token);
      } catch (_) {}
      setUser(res.user);
      return res.user;
    },
    [setUser]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      // Raw capability (holds the admin role at all) — drives whether the
      // role switcher is offered. `isAdminView` below is the *acting-as*
      // flag the UI should gate admin surfaces on.
      canBeAdmin: !!user?.is_admin,
      activeRole: resolveActiveRole(user),
      isAdminView: !!user?.is_admin && resolveActiveRole(user) === "ADMIN",
      availableRoles: Array.isArray(user?.app_roles) ? user.app_roles : [],
      switchRole,
      isAdmin: !!user?.is_admin,
      isItAdmin: user?.admin_role === "IT_ADMIN",
      isImpersonating: !!impersonatorId,
      impersonatorId,
      setUser,
      logout: () => {
        setUser(null);
        if (window.location.hash && window.location.hash !== "#/") {
          window.location.hash = "#/";
        }
      },
    }),
    [user, impersonatorId, setUser, switchRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

export function useCurrentUser() {
  return useAuth().user;
}
